const REGION_CURRENCY = {
  uk: { symbol: '\u00a3', code: 'GBP', locale: 'en-GB', settlement: 'GBP' },
  nigeria: { symbol: '\u20a6', code: 'NGN', locale: 'en-NG', settlement: 'NGN', showUsdRef: true },
  ghana: { symbol: 'GH\u20b5', code: 'GHS', locale: 'en-GH', settlement: 'GHS' },
  // Kenya settles in GBP (distributor buy prices are GBP ex VAT).
  // ceo_instruction_ref: kenya_calculator_create_quote_build_22jul2026
  kenya: { symbol: '\u00a3', code: 'GBP', locale: 'en-GB', settlement: 'GBP' },
  dubai: { symbol: 'AED', code: 'AED', locale: 'ar-AE', settlement: 'AED' },
  uae: { symbol: 'AED', code: 'AED', locale: 'ar-AE', settlement: 'AED' },
  international: { symbol: '$', code: 'USD', locale: 'en-US', settlement: 'USD' },
};

// Approximate exchange rates to USD (for reference display only)
const TO_USD_RATES = {
  NGN: 0.00065,
  GHS: 0.065,
  GBP: 1.27,
  USD: 1,
};

const UNKNOWN_CURRENCY = { symbol: '\u2014', code: 'UNKNOWN', locale: 'en-GB', settlement: 'UNKNOWN' };

export function getCurrency(region) {
  if (region === null || region === undefined || region === '') {
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[currency.getCurrency] called with null/undefined region — returning UNKNOWN. Caller must pass an explicit region key.');
    }
    return UNKNOWN_CURRENCY;
  }
  const mapped = REGION_CURRENCY[normaliseRegion(region)];
  if (!mapped) {
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[currency.getCurrency] unknown region "${region}" — returning UNKNOWN. Add it to REGION_CURRENCY or fix the caller.`);
    }
    return UNKNOWN_CURRENCY;
  }
  return mapped;
}

export function normaliseRegion(region) {
  if (!region) return region;
  if (region === 'ng' || region === 'NG') return 'nigeria';
  return region;
}

export function formatPrice(amount, region) {
  const { symbol } = getCurrency(region);
  return `${symbol}${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatPriceDecimal(amount, region) {
  const { symbol } = getCurrency(region);
  return `${symbol}${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function compactPrice(amount, region) {
  const { symbol } = getCurrency(region);
  const n = amount || 0;
  if (n >= 1000000) return `${symbol}${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${symbol}${Math.round(n / 1000)}k`;
  return `${symbol}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Format price with optional USD reference for non-USD regions.
 * Nigeria: "₦95,000 (~$62)"
 * UK: "£47.50"
 * International: "$62.00"
 */
export function formatPriceWithRef(amount, region) {
  const curr = getCurrency(region);
  const primary = `${curr.symbol}${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (curr.showUsdRef && amount && curr.code !== 'USD') {
    const rate = TO_USD_RATES[curr.code] || 1;
    const usdVal = Math.round(amount * rate);
    if (usdVal > 0) return { primary, ref: `~$${usdVal.toLocaleString()} USD`, full: `${primary} (~$${usdVal})` };
  }

  return { primary, ref: null, full: primary };
}

/**
 * Get the settlement currency label for a region.
 * e.g., "Prices in GBP (£)" or "Prices in NGN (₦)"
 */
export function getSettlementLabel(region) {
  const curr = getCurrency(region);
  return `Prices in ${curr.code} (${curr.symbol})`;
}

// currency is a property of the RECORD, never the viewer, page or a literal.
// ceo_instruction_ref: money_resolver_19sep2026
const CODE_TO_REGION = { GBP: 'uk', NGN: 'nigeria', GHS: 'ghana', AED: 'dubai', USD: 'international' };
// ISO codes with no region book (expenses etc) still format via the resolver
const CODE_SYMBOLS = { EUR: '\u20ac', KES: 'KSh ', ZAR: 'R', CAD: 'CA$' };

function formatByCode(amount, code) {
  const sym = CODE_SYMBOLS[code];
  if (!sym) return null;
  return `${sym}${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function resolveMoneyRegion(record) {
  if (!record) return null;
  // ng_usd_quotes_23sep2026 — an explicit currency code wins over region so a
  // USD export quote/order on a Nigeria account renders $ not ₦.
  const code = String(record.currency || '').toUpperCase();
  if (CODE_TO_REGION[code]) return CODE_TO_REGION[code];
  const direct = normaliseRegion(record.region);
  if (direct && REGION_CURRENCY[direct]) return direct;
  const owner = normaliseRegion(record.salon_region);
  if (owner && REGION_CURRENCY[owner]) return owner;
  return null;
}

// currency_truth_23sep2026 (LOCKED RULE): a document renders the currency it
// was RAISED in, for ever. Doc-driven symbols and per-currency totals.
const DOC_SYMBOLS = { GBP: '\u00a3', NGN: '\u20a6', USD: '$', GHS: 'GH\u20b5', AED: 'AED', EUR: '\u20ac', KES: 'KSh ', ZAR: 'R', CAD: 'CA$' };

export function docSymbol(doc, fallbackSymbol = '') {
  const code = String(doc?.currency || '').toUpperCase();
  return DOC_SYMBOLS[code] || fallbackSymbol;
}

export function sumByCurrency(docs, amountOf, homeCode) {
  const by = {};
  (docs || []).forEach((d) => {
    const code = String(d?.currency || homeCode || '').toUpperCase() || 'UNKNOWN';
    const v = Number(amountOf(d) || 0);
    if (!v) return;
    by[code] = Math.round(((by[code] || 0) + v) * 100) / 100;
  });
  return by;
}

export function formatByCurrency(by, fallbackSymbol = '') {
  const entries = Object.entries(by || {}).filter(([, v]) => v);
  if (!entries.length) return `${fallbackSymbol}0.00`;
  return entries
    .map(([code, v]) => `${DOC_SYMBOLS[code] || fallbackSymbol}${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
    .join(' + ');
}

export function money(amount, record) {
  const region = resolveMoneyRegion(record);
  if (!region) {
    const byCode = formatByCode(amount, String(record?.currency || '').toUpperCase());
    if (byCode) return byCode;
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[currency.money] record has no resolvable region — rendering UNKNOWN', record);
    }
    return `\u2014${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return formatPrice(amount, region);
}

export function moneyCompact(amount, record) {
  const region = resolveMoneyRegion(record);
  if (!region) {
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[currency.moneyCompact] record has no resolvable region — rendering UNKNOWN', record);
    }
    const n = amount || 0;
    if (n >= 1000000) return `\u2014${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `\u2014${Math.round(n / 1000)}k`;
    return `\u2014${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return compactPrice(amount, region);
}

export function moneyByRegion(byRegion, compact = true) {
  const fmt = compact ? moneyCompact : money;
  const entries = Object.entries(byRegion || {});
  if (!entries.length) return '0';
  const nonZero = entries.filter(([, v]) => v);
  const shown = nonZero.length ? nonZero : [entries[0]];
  return shown.map(([r, v]) => fmt(v, { region: r })).join(' \u00b7 ');
}

/**
 * Map a salon's country field to a currency region key.
 * e.g., "Nigeria" → "nigeria", "United Kingdom" → "uk", "Dubai" → "dubai"
 */
export function countryToRegion(country) {
  if (!country) return 'international';
  const c = country.toLowerCase().trim();
  if (c === 'nigeria' || c === 'ng') return 'nigeria';
  if (c === 'united kingdom' || c === 'uk' || c === 'gb' || c === 'england') return 'uk';
  if (c === 'ghana' || c === 'gh') return 'ghana';
  if (c === 'dubai' || c === 'uae' || c === 'united arab emirates') return 'dubai';
  return 'international';
}

/**
 * Get currency for a salon based on its country field.
 * This is the SINGLE SOURCE OF TRUTH for order currency.
 */
export function getSalonCurrency(salonCountry) {
  const region = countryToRegion(salonCountry);
  return getCurrency(region);
}

